import { Auth } from "aws-amplify";
import { emptyV2Api as api } from "../emptyV2Api";

export interface WsProduct {
  Id: string;
  productId: string;
  variantId?: string;
  title: string;
  productTitle: string;
  productName? : string;
  description?: string;
  imageUrl?: string;
  status: string;
  country?: string;
  regions?: string[];
  coverageCountries?: string[];
  network?: string;
  localProductTag?: boolean;
  createdAt?: string;
  updatedAt?: string;
  productPromotions?: ProductPromotion;
  productTags?: string;
  summary?: WsProductSummary;
  productLink?: string;
}

export interface WsProductSummary {
  data_min?: number;
  data_max?: number;
  validity_min?: number;
  validity_max?: number;
  price_min?: number;
  price_max?: number;
  calls_min?: number;
  calls_max?: number;
}


export interface ProductPromotion {
  tag?: string;
  dataTag?: {
    label: string;
    value: string;
  };
  priceTag?: {
    value: string;
    unit: "PERCENT" | "FIXED" | "";
  };
}

export interface WsProductVariant {
  Id: string;
  productId: string;
  variantId: string;
  name: string;
  operator?: string;
  price: number;
  stock?: number;
  status: string;
  dataGb?: string;
  bonusData? : string;
  promotionalTag? : string;
  dataPlan?: string;
  duration?: number;
  calls?: string;
  eKyc?: string;
  internalSku?: string;
  talkTime?: string;
  tiktokSupported?: boolean;
  validity?: any;
  activationPolicy?: string;
  createdAt?: string;
  updatedAt?: string;
  description?  : string;
}

export interface WsProductWithVariantsResponse {
  product: WsProduct;
  variants: WsProductVariant[];
}

export interface BulkUpdateRequest {
  [productId: string]: {
    productTags?: string;
    productPromotions?: ProductPromotion;
  };
}

export type WsProductExportKind = "LINKS";
export type WsProductLinkScope = "ALL_COUNTRIES" | "ONE_PER_PRODUCT";
export type WsProductExportFormat = "CSV" | "EXCEL";

export type WebshopAssetFolder = "products" | "network";

export interface DeleteWebshopAssetRequest {
  key?: string;
  filename?: string;
  folder?: WebshopAssetFolder;
  url?: string;
}

export interface DeleteWebshopAssetResponse {
  deleted: string[];
  message?: string;
}

export const addTagTypes = ["WsProducts"] as const;

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getWsProducts: build.query<WsProduct[], void>({
        query: () => ({
          url: "webshop/products/manage?get_all=true",
          method: "GET",
        }),
        providesTags: ["WsProducts"],
      }),
      getWsProductWithVariants: build.query<WsProductWithVariantsResponse, string>({
        query: (productId) => ({
          url: `webshop/products/manage?productId=${encodeURIComponent(productId)}&variants=true`,
          method: "GET",
        }),
        providesTags: ["WsProducts"],
      }),
      createWsProduct: build.mutation<any, WsProduct>({
        query: (product) => ({
          url: "webshop/products/manage",
          method: "POST",
          body: product,
        }),
        invalidatesTags: ["WsProducts"],
      }),
      updateWsProduct: build.mutation<any, WsProduct>({
        query: (product) => ({
          url: "webshop/products/manage",
          method: "PUT",
          body: product,
        }),
        invalidatesTags: ["WsProducts"],
      }),
      getUpdatedSummary: build.query<WsProduct | WsProductSummary, string>({
        query: (productId) => ({
          url: `webshop/products/manage?action=recalculate&productId=${encodeURIComponent(productId)}`,
          method: "GET",
        }),
      }),
      getAllWsVariants: build.query<WsProductVariant[], void>({
        query: () => ({
          url: "webshop/variants",
          method: "GET",
        }),
        providesTags: ["WsProducts"],
      }),
      transferWsVariants: build.mutation<any, { productId: string; variantIds: string[] }>({
        query: (payload) => ({
          url: "webshop/products/manage/transfer-variants",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["WsProducts"],
      }),
      bulkUpdateProducts: build.mutation<BulkUpdateRequest, any>({
        query: (payload) => ({
          url: "webshop/products/manage?action=bulk-update",
          method: "PUT",
          body: payload,
        }),
        invalidatesTags: ["WsProducts"],
      }),
      bulkUpdateVariants: build.mutation<any, { action: string; variantIds: string[]; productId?: string }>({
        query: (payload) => ({
          url: "webshop/variants/bulk-update",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["WsProducts"],
      }),
      updateWsVariant: build.mutation<any, WsProductVariant>({
        query: (variant) => ({
          url: `webshop/products/manage?variantId=${variant.variantId}`,
          method: "PUT",
          body: variant,
        }),
        invalidatesTags: ["WsProducts"],
      }),
      deleteWsProduct: build.mutation<any, string>({
        query: (productId) => ({
          url: `webshop/products/manage?productId=${encodeURIComponent(productId)}`,
          method: "DELETE",
        }),
        invalidatesTags: ["WsProducts"],
      }),
      deleteWsVariant: build.mutation<any, { productId: string; variantId: string }>({
        query: ({ productId, variantId }) => ({
          url: `webshop/products/manage?productId=${encodeURIComponent(productId)}&variantId=${encodeURIComponent(variantId)}`,
          method: "DELETE",
        }),
        invalidatesTags: ["WsProducts"],
      }),
      deleteWebshopAsset: build.mutation<
        DeleteWebshopAssetResponse,
        DeleteWebshopAssetRequest
      >({
        async queryFn(arg, _api, _extra, fetchWithBQ) {
          let token: string;
          try {
            const session = await Auth.currentSession();
            token = session.getIdToken().getJwtToken();
          } catch {
            return {
              error: { status: "CUSTOM_ERROR", error: "Not authenticated" },
            };
          }

          const params = new URLSearchParams({ action: "webshop-assets" });
          if (arg.folder && arg.filename && !arg.key && !arg.url) {
            params.set("folder", arg.folder);
          }

          const body = arg.key
            ? { key: arg.key }
            : arg.url
              ? { url: arg.url }
              : { filename: arg.filename };

          const result = await fetchWithBQ({
            url: `webshop/products/manage?${params.toString()}`,
            method: "DELETE",
            body,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (result.error) return { error: result.error };
          return { data: result.data as DeleteWebshopAssetResponse };
        },
      }),
      getWebshopUploadJobs: build.query<any[], void>({
        query: () => ({
          url: "webshop/products/upload",
          method: "GET",
        }),
        providesTags: ["WsProducts"],
      }),
    }),
  });

export const {
  useGetWsProductsQuery,
  useGetUpdatedSummaryQuery,
  useLazyGetUpdatedSummaryQuery,
  useGetWsProductWithVariantsQuery,
  useCreateWsProductMutation,
  useUpdateWsProductMutation,
  useGetAllWsVariantsQuery,
  useTransferWsVariantsMutation,
  useBulkUpdateVariantsMutation,
  useBulkUpdateProductsMutation,
  useUpdateWsVariantMutation,
  useDeleteWsProductMutation,
  useDeleteWsVariantMutation,
  useDeleteWebshopAssetMutation,
  useGetWebshopUploadJobsQuery,
} = injectedRtkApi;

